/**
 * O repositório: tudo que os aplicativos sabem fazer com os dados.
 *
 * É a única porta. As telas não tocam o depósito e não recalculam status por
 * conta própria — pedem aqui, e aqui as regras de `@pcp/dominio` são
 * aplicadas do mesmo jeito no escritório e no tablet.
 *
 * **Escrever é sempre gravar um evento ou um registro, nunca um estado.** Não
 * existe `mudarStatusDaOs`. Existe "a etapa X começou às 14:02" — e o status
 * aparece sozinho, do mesmo jeito, em qualquer aparelho que tenha os mesmos
 * eventos (PRD RN-020, RN-030).
 */

import {
  avisosAoIniciar,
  estadoDaEtapa,
  estadoDaOs,
  novoId,
  ordenarEtapasDaOs,
  ordenarFila,
  osAtrasada,
  osEmRisco,
  segundosUteisEntre,
  temBloqueio,
  type Alerta,
  type Aviso,
  type Calendario,
  type Cliente,
  type DeParaEstacao,
  type Dispositivo,
  type EstadoEtapa,
  type EstadoOs,
  type Estacao,
  type EventoProducao,
  type Expedicao,
  type Importacao,
  type ItemDaFila,
  type MotivoPausa,
  type Os,
  type OsEntregaPrevista,
  type OsEtapa,
  type OsLida,
  type OsMaterial,
  type OsPlano,
  type OsVersao,
  type Parametros,
  type Pessoa,
  type Secao,
  type Setor,
  type StatusOs,
  type TipoAlerta,
  type TipoEvento,
  type TipoExpedicao,
  type TipoLinha,
} from '@pcp/dominio';
import { classificar } from './de-para.js';
import { importarOs, type ResultadoImportacao } from './importar.js';
import { semente, type Semente } from './semente.js';
import { COLECOES, type Deposito } from './deposito.js';

/* ------------------------------------------------------------------ */
/* O que as telas recebem                                              */
/* ------------------------------------------------------------------ */

export interface LinhaCarteira {
  os: Os;
  estado: EstadoOs;
  atrasada: boolean;
  emRisco: boolean;
  setorAtual: Setor | null;
  estacaoAtual: Estacao | null;
  etapaAtual: OsEtapa | null;
  operadores: string[];
  temEtapaAClassificar: boolean;
  temEtapaMarcada: boolean;
}

export interface DetalheOs extends LinhaCarteira {
  planos: OsPlano[];
  etapas: Array<{ etapa: OsEtapa; estado: EstadoEtapa; estacao: Estacao | null; setor: Setor | null }>;
  materiais: OsMaterial[];
  entregas: OsEntregaPrevista[];
  versoes: OsVersao[];
  eventos: EventoProducao[];
  expedicoes: Expedicao[];
  cliente: Cliente | null;
}

export interface ColunaDoQuadro {
  setor: Setor;
  itens: ItemDaFila[];
  emExecucao: number;
}

export interface Painel {
  atrasadas: LinhaCarteira[];
  vencemHoje: LinhaCarteira[];
  emRisco: LinhaCarteira[];
  emProducao: LinhaCarteira[];
  emFila: LinhaCarteira[];
  prontas: LinhaCarteira[];
  cargaPorSetor: Array<{ setor: Setor; segundosEmFila: number; etapas: number; emExecucao: number }>;
  apontamentosDoDia: number;
  alertasAbertos: number;
  importacoesEmQuarentena: number;
  etapasAClassificar: number;
}

export interface ItemDeTriagem {
  chave: string;
  secao: Secao;
  equipamentoOs: string | null;
  nomeOs: string;
  etapas: OsEtapa[];
  osNumeros: number[];
}

export interface ResultadoDaImportacao {
  importacao: Importacao;
  resultado: ResultadoImportacao | null;
  motivoQuarentena: string | null;
  avisos: string[];
}

export interface RespostaDeEvento {
  aceito: boolean;
  motivo: string | null;
  avisos: Aviso[];
  evento: EventoProducao | null;
}

interface Arquivo {
  ref: string;
  tipo: string;
  dados: Uint8Array;
  criadoEm: string;
}

interface Config<T> {
  chave: string;
  valor: T;
}

/* ------------------------------------------------------------------ */

/**
 * Uma ação que, além de mudar o banco local, precisa acontecer no SERVIDOR.
 *
 * O `Repositorio` não conhece Supabase — ele avisa o que aconteceu e quem
 * escuta (o aplicativo) enfileira e envia. É o que mantém `dados` sem
 * dependência de rede, e o escritório online sem espalhar `fetch` pelas telas.
 */
export interface AcaoDeServidor {
  /** O tipo da ação; vira o nome da RPC do outro lado. */
  tipo: string;
  /** A chave do item na fila (idempotência). */
  id: string;
  /** O corpo da RPC. */
  carga: unknown;
}

export class Repositorio {
  private cadastros: Semente | null = null;
  private indice: Indice | null = null;
  private ouvintes = new Set<() => void>();

  /**
   * Para onde mandar a ação quando ela também precisa valer no servidor.
   * Fica `null` quando não há nuvem — e aí o sistema é só local, sem erro.
   */
  aoServidor: ((acao: AcaoDeServidor) => void) | null = null;

  /** Sobe um arquivo para o Storage e devolve o caminho. `null` = guardar local. */
  aoEnviarArquivo: ((tipo: string, dados: Uint8Array) => Promise<string>) | null = null;

  /** Baixa um arquivo do Storage pelo caminho. */
  aoBaixarArquivo:
    | ((ref: string) => Promise<{ tipo: string; dados: Uint8Array } | null>)
    | null = null;

  constructor(private deposito: Deposito) {}

  private enviar(tipo: string, id: string, carga: unknown): void {
    this.aoServidor?.({ tipo, id, carga });
  }

  /* ---------------- ciclo de vida ---------------- */

  /** Garante que a fábrica está cadastrada. Reaplicar não duplica nada. */
  async semear(forcar = false): Promise<void> {
    const jaTem = (await this.deposito.listar<Setor>('setor')).length > 0;
    if (jaTem && !forcar) return;

    const s = semente();
    await this.deposito.gravarLote('setor', s.setores);
    await this.deposito.gravarLote('estacao', s.estacoes);
    await this.deposito.gravarLote('dePara', s.dePara);
    await this.deposito.gravarLote('motivo', s.motivos);
    await this.deposito.gravarLote('pessoa', s.pessoas);
    await this.gravarConfig('parametros', s.parametros);
    await this.gravarConfig('calendario', s.calendario);
    this.invalidar();
  }

  /** Avisa quando algo muda, para a tela redesenhar. */
  aoMudar(ouvinte: () => void): () => void {
    this.ouvintes.add(ouvinte);
    return () => this.ouvintes.delete(ouvinte);
  }

  /**
   * Esquece os derivados; a próxima leitura recalcula.
   *
   * Usado quando o SERVIDOR trouxe estado novo (o sincronismo escreveu no
   * depósito por fora) — sem isto, a tela mostraria o que estava antes.
   */
  recarregar(): void {
    this.invalidar();
  }

  private invalidar(): void {
    this.cadastros = null;
    this.indice = null;
    for (const o of this.ouvintes) o();
  }

  /** Só o índice derivado; cadastros seguem válidos. */
  private invalidarProducao(): void {
    this.indice = null;
    for (const o of this.ouvintes) o();
  }

  /* ---------------- cadastros ---------------- */

  async carregarCadastros(): Promise<Semente> {
    if (this.cadastros) return this.cadastros;
    const [setores, estacoes, dePara, motivos, pessoas, parametros, calendario] = await Promise.all([
      this.deposito.listar<Setor>('setor'),
      this.deposito.listar<Estacao>('estacao'),
      this.deposito.listar<DeParaEstacao>('dePara'),
      this.deposito.listar<MotivoPausa>('motivo'),
      this.deposito.listar<Pessoa>('pessoa'),
      this.lerConfig<Parametros>('parametros'),
      this.lerConfig<Calendario>('calendario'),
    ]);
    const padrao = semente();
    this.cadastros = {
      setores: setores.sort((a, b) => a.ordem - b.ordem),
      estacoes,
      dePara,
      motivos,
      pessoas,
      parametros: parametros ?? padrao.parametros,
      calendario: calendario ?? padrao.calendario,
    };
    return this.cadastros;
  }

  async lerConfig<T>(chave: string): Promise<T | null> {
    const c = await this.deposito.ler<Config<T>>('config', chave);
    return c ? c.valor : null;
  }

  async gravarConfig<T>(chave: string, valor: T): Promise<void> {
    await this.deposito.gravar<Config<T>>('config', { chave, valor });
    this.invalidar();
  }

  async salvarParametros(p: Parametros): Promise<void> {
    await this.gravarConfig('parametros', p);
    this.enviar('parametros_salvar', 'parametros', { p });
  }

  async salvarCalendario(c: Calendario): Promise<void> {
    await this.gravarConfig('calendario', c);
    this.enviar('calendario_salvar', 'calendario', { p_dias: c.dias, p_feriados: c.feriados });
  }

  async salvarSetor(s: Setor): Promise<void> {
    await this.deposito.gravar('setor', s);
    this.invalidar();
    this.enviar('setor_salvar', s.id, { p: s });
  }

  async salvarEstacao(e: Estacao): Promise<void> {
    await this.deposito.gravar('estacao', e);
    this.invalidar();
    this.enviar('estacao_salvar', e.id, { p: e });
  }

  async salvarMotivo(m: MotivoPausa): Promise<void> {
    await this.deposito.gravar('motivo', m);
    this.invalidar();
    this.enviar('motivo_salvar', m.id, { p: m });
  }

  async salvarPessoa(p: Pessoa): Promise<void> {
    await this.deposito.gravar('pessoa', p);
    this.invalidar();
    this.enviar('pessoa_salvar', p.id, { p });
  }

  async salvarDispositivo(d: Dispositivo): Promise<void> {
    await this.deposito.gravar('dispositivo', d);
    this.invalidar();
    this.enviar('dispositivo_salvar', d.id, { p: d });
  }

  async dispositivos(): Promise<Dispositivo[]> {
    return this.deposito.listar<Dispositivo>('dispositivo');
  }

  async salvarDePara(r: DeParaEstacao): Promise<void> {
    await this.deposito.gravar('dePara', r);
    this.invalidar();
    this.enviar('de_para_salvar', r.id, { p: r });
  }

  async apagarDePara(id: string): Promise<void> {
    await this.deposito.apagar('dePara', id);
    this.invalidar();
    this.enviar('de_para_apagar', id, { p_id: id });
  }

  /* ---------------- índice derivado ---------------- */

  /**
   * Carrega tudo que a produção precisa e calcula os estados uma vez.
   *
   * Recalcular por tela seria fácil de escrever e caro de usar: a carteira, o
   * quadro e o painel pedem o estado das mesmas OS ao mesmo tempo. Aqui o
   * cálculo acontece uma vez e é invalidado quando alguém escreve.
   */
  private async carregarIndice(): Promise<Indice> {
    if (this.indice) return this.indice;
    const cadastros = await this.carregarCadastros();

    const [oss, etapas, eventos] = await Promise.all([
      this.deposito.listar<Os>('os'),
      this.deposito.listar<OsEtapa>('osEtapa'),
      this.deposito.listar<EventoProducao>('evento'),
    ]);

    const eventosPorEtapa = new Map<string, EventoProducao[]>();
    for (const e of eventos) {
      const lista = eventosPorEtapa.get(e.osEtapaId);
      if (lista) lista.push(e);
      else eventosPorEtapa.set(e.osEtapaId, [e]);
    }

    const etapasPorOs = new Map<number, OsEtapa[]>();
    for (const e of etapas) {
      const lista = etapasPorOs.get(e.osNumero);
      if (lista) lista.push(e);
      else etapasPorOs.set(e.osNumero, [e]);
    }

    const agora = new Date().toISOString();
    const estadosEtapa = new Map<string, EstadoEtapa>();
    for (const etapa of etapas) {
      estadosEtapa.set(
        etapa.id,
        estadoDaEtapa(eventosPorEtapa.get(etapa.id) ?? [], {
          agora,
          quantidadePrevista: etapa.impressoesPrevistas,
          parametros: cadastros.parametros,
        }),
      );
    }

    const estadosOs = new Map<number, EstadoOs>();
    for (const os of oss) {
      estadosOs.set(
        os.numero,
        estadoDaOs(os, etapasPorOs.get(os.numero) ?? [], { agora, estados: estadosEtapa }),
      );
    }

    this.indice = {
      agora,
      oss,
      etapasPorOs,
      eventosPorEtapa,
      estadosEtapa,
      estadosOs,
      cadastros,
    };
    return this.indice;
  }

  /* ---------------- leitura ---------------- */

  async carteira(): Promise<LinhaCarteira[]> {
    const i = await this.carregarIndice();
    return i.oss.map((os) => this.montarLinha(i, os)).sort(ordenarCarteira);
  }

  private montarLinha(i: Indice, os: Os): LinhaCarteira {
    const estado = i.estadosOs.get(os.numero) ?? vazio();
    const etapas = i.etapasPorOs.get(os.numero) ?? [];
    const etapaAtual = etapas.find((e) => e.id === estado.etapaAtualId) ?? null;
    const estacaoAtual = etapaAtual?.estacaoId
      ? (i.cadastros.estacoes.find((e) => e.id === etapaAtual.estacaoId) ?? null)
      : null;
    const setorAtual = estacaoAtual
      ? (i.cadastros.setores.find((s) => s.id === estacaoAtual.setorId) ?? null)
      : null;
    const estadoEtapa = etapaAtual ? i.estadosEtapa.get(etapaAtual.id) : undefined;

    return {
      os,
      estado,
      atrasada: osAtrasada(os, estado.status, i.cadastros.parametros, i.agora),
      emRisco: osEmRisco(
        os.previsaoEntregaEm,
        estado.tempoPrevistoRestanteSeg,
        (de, ate) => segundosUteisEntre(de, ate, i.cadastros.calendario),
        i.agora,
      ),
      setorAtual,
      estacaoAtual,
      etapaAtual,
      operadores: estadoEtapa?.operadores ?? [],
      temEtapaAClassificar: etapas.some((e) => e.aClassificar && e.tipoLinha === 'operacao'),
      temEtapaMarcada: etapas.some((e) => e.marcadaVerificar),
    };
  }

  async detalhe(numero: number): Promise<DetalheOs | null> {
    const i = await this.carregarIndice();
    const os = i.oss.find((o) => o.numero === numero);
    if (!os) return null;

    const [planos, materiais, entregas, versoes, expedicoes] = await Promise.all([
      this.deposito.porIndice<OsPlano>('osPlano', 'osNumero', numero),
      this.deposito.porIndice<OsMaterial>('osMaterial', 'osNumero', numero),
      this.deposito.porIndice<OsEntregaPrevista>('osEntrega', 'osNumero', numero),
      this.deposito.porIndice<OsVersao>('osVersao', 'osNumero', numero),
      this.deposito.porIndice<Expedicao>('expedicao', 'osNumero', numero),
    ]);

    const etapas = ordenarEtapasDaOs(i.etapasPorOs.get(numero) ?? []);
    const eventos = etapas.flatMap((e) => i.eventosPorEtapa.get(e.id) ?? []);
    const cliente = os.clienteId
      ? await this.deposito.ler<Cliente>('cliente', os.clienteId)
      : null;

    return {
      ...this.montarLinha(i, os),
      planos: planos.sort((a, b) => a.numero - b.numero),
      etapas: etapas.map((etapa) => {
        const estacao = etapa.estacaoId
          ? (i.cadastros.estacoes.find((x) => x.id === etapa.estacaoId) ?? null)
          : null;
        return {
          etapa,
          estado: i.estadosEtapa.get(etapa.id) ?? estadoVazioDeEtapa(),
          estacao,
          setor: estacao
            ? (i.cadastros.setores.find((s) => s.id === estacao.setorId) ?? null)
            : null,
        };
      }),
      materiais: materiais.sort((a, b) => a.ordem - b.ordem),
      entregas,
      versoes: versoes.sort((a, b) => b.versao - a.versao),
      eventos: eventos.sort((a, b) => Date.parse(a.ocorridoEm) - Date.parse(b.ocorridoEm)),
      expedicoes,
      cliente,
    };
  }

  /** A fila de uma estação: só o que alguém pode começar ou está tocando. */
  async filaDaEstacao(estacaoId: string): Promise<ItemDaFila[]> {
    return this.filaPor((e) => e.estacaoId === estacaoId);
  }

  async filaDoSetor(setorId: string): Promise<ItemDaFila[]> {
    return this.filaPor((e) => e.setorId === setorId);
  }

  private async filaPor(criterio: (e: OsEtapa) => boolean): Promise<ItemDaFila[]> {
    const i = await this.carregarIndice();
    const itens: ItemDaFila[] = [];
    for (const os of i.oss) {
      // Encerrada some da fila — inclusive a que chegou concluída no ERP.
      const estadoOs = i.estadosOs.get(os.numero);
      if (estadoOs?.status === 'concluida' || estadoOs?.status === 'cancelada') continue;
      for (const etapa of i.etapasPorOs.get(os.numero) ?? []) {
        if (etapa.tipoLinha !== 'operacao' || !criterio(etapa)) continue;
        const estado = i.estadosEtapa.get(etapa.id) ?? estadoVazioDeEtapa();
        if (estado.status === 'concluida' || estado.status === 'pulada') continue;
        itens.push({ etapa, os, estado });
      }
    }
    return ordenarFila(itens);
  }

  async quadro(): Promise<ColunaDoQuadro[]> {
    const i = await this.carregarIndice();
    const colunas: ColunaDoQuadro[] = [];
    for (const setor of i.cadastros.setores) {
      const itens = await this.filaDoSetor(setor.id);
      colunas.push({
        setor,
        itens,
        emExecucao: itens.filter((x) => x.estado.status === 'em_execucao').length,
      });
    }
    return colunas;
  }

  async painel(): Promise<Painel> {
    const i = await this.carregarIndice();
    const linhas = i.oss.map((os) => this.montarLinha(i, os));
    const hoje = i.agora.slice(0, 10);

    const abertas = linhas.filter(
      (l) => l.estado.status !== 'concluida' && l.estado.status !== 'cancelada',
    );

    const cargaPorSetor = i.cadastros.setores.map((setor) => {
      let segundos = 0;
      let etapas = 0;
      let emExecucao = 0;
      for (const os of i.oss) {
        // Encerrada não pesa na carga — inclusive a concluída no ERP.
        const estadoOs = i.estadosOs.get(os.numero);
        if (estadoOs?.status === 'concluida' || estadoOs?.status === 'cancelada') continue;
        for (const etapa of i.etapasPorOs.get(os.numero) ?? []) {
          if (etapa.setorId !== setor.id || etapa.tipoLinha !== 'operacao') continue;
          const estado = i.estadosEtapa.get(etapa.id);
          if (estado?.status === 'concluida' || estado?.status === 'pulada') continue;
          segundos += etapa.tempoPrevistoSeg ?? 0;
          etapas += 1;
          if (estado?.status === 'em_execucao') emExecucao += 1;
        }
      }
      return { setor, segundosEmFila: segundos, etapas, emExecucao };
    });

    const eventos = await this.deposito.listar<EventoProducao>('evento');
    const alertas = await this.alertas();
    const importacoes = await this.deposito.listar<Importacao>('importacao');

    return {
      atrasadas: abertas.filter((l) => l.atrasada).sort(porPrazo),
      vencemHoje: abertas
        .filter((l) => !l.atrasada && l.os.previsaoEntregaEm.slice(0, 10) <= hoje)
        .sort(porPrazo),
      emRisco: abertas.filter((l) => l.emRisco && !l.atrasada).sort(porPrazo),
      emProducao: abertas.filter((l) => l.estado.status === 'em_producao').sort(porPrazo),
      emFila: abertas.filter((l) => l.estado.status === 'em_fila').sort(porPrazo),
      prontas: linhas.filter((l) => l.estado.status === 'pronta').sort(porPrazo),
      cargaPorSetor,
      apontamentosDoDia: eventos.filter((e) => e.ocorridoEm.slice(0, 10) === hoje).length,
      alertasAbertos: alertas.length,
      importacoesEmQuarentena: importacoes.filter((x) => x.status === 'quarentena').length,
      etapasAClassificar: (await this.triagem()).length,
    };
  }

  /** Textos que ninguém ensinou ainda, agrupados para classificar de uma vez. */
  async triagem(): Promise<ItemDeTriagem[]> {
    const i = await this.carregarIndice();
    const porChave = new Map<string, ItemDeTriagem>();
    for (const os of i.oss) {
      if (os.canceladaEm) continue;
      for (const etapa of i.etapasPorOs.get(os.numero) ?? []) {
        if (!etapa.aClassificar) continue;
        const chave = `${etapa.secao}|${etapa.equipamentoOs ?? ''}|${etapa.nomeOs}`;
        const item = porChave.get(chave);
        if (item) {
          item.etapas.push(etapa);
          if (!item.osNumeros.includes(os.numero)) item.osNumeros.push(os.numero);
        } else {
          porChave.set(chave, {
            chave,
            secao: etapa.secao,
            equipamentoOs: etapa.equipamentoOs,
            nomeOs: etapa.nomeOs,
            etapas: [etapa],
            osNumeros: [os.numero],
          });
        }
      }
    }
    return [...porChave.values()].sort((a, b) => b.etapas.length - a.etapas.length);
  }

  async alertas(incluirResolvidos = false): Promise<Alerta[]> {
    const todos = await this.deposito.listar<Alerta>('alerta');
    const lista = incluirResolvidos ? todos : todos.filter((a) => a.resolvidoEm === null);
    return lista.sort((a, b) => Date.parse(b.criadoEm) - Date.parse(a.criadoEm));
  }

  /** Todas as saídas já registradas, da mais recente para a mais antiga. */
  async expedicoes(): Promise<Expedicao[]> {
    const todas = await this.deposito.listar<Expedicao>('expedicao');
    return todas.sort((a, b) => Date.parse(b.ocorridoEm) - Date.parse(a.ocorridoEm));
  }

  async importacoes(): Promise<Importacao[]> {
    const todas = await this.deposito.listar<Importacao>('importacao');
    return todas.sort((a, b) => Date.parse(b.recebidoEm) - Date.parse(a.recebidoEm));
  }

  async eventosDaEtapa(etapaId: string): Promise<EventoProducao[]> {
    const i = await this.carregarIndice();
    return [...(i.eventosPorEtapa.get(etapaId) ?? [])].sort(
      (a, b) => Date.parse(a.ocorridoEm) - Date.parse(b.ocorridoEm),
    );
  }

  async todosOsEventos(): Promise<EventoProducao[]> {
    return this.deposito.listar<EventoProducao>('evento');
  }

  async arquivo(ref: string): Promise<Arquivo | null> {
    // Caminho do Storage (escritório online): baixa os bytes. O ref local
    // começa com `arq-`; o do Storage é um caminho do balde.
    if (this.aoBaixarArquivo && !ref.startsWith('arq-')) {
      const b = await this.aoBaixarArquivo(ref);
      return b ? { ref, tipo: b.tipo, dados: b.dados, criadoEm: '' } : null;
    }
    return this.deposito.ler<Arquivo>('arquivo', ref);
  }

  async guardarArquivo(tipo: string, dados: Uint8Array): Promise<string> {
    // Online: sobe para o Storage e devolve o caminho. Local (testes, ou sem
    // servidor): guarda os bytes no depósito.
    if (this.aoEnviarArquivo) {
      return this.aoEnviarArquivo(tipo, dados);
    }
    const ref = `arq-${novoId()}`;
    await this.deposito.gravar<Arquivo>('arquivo', {
      ref,
      tipo,
      dados,
      criadoEm: new Date().toISOString(),
    });
    return ref;
  }

  /* ---------------- importação ---------------- */

  /**
   * Recebe uma OS já lida e aplica. O leitor roda antes (no navegador ou no
   * agente) porque ele depende do pdf.js, que não deve entrar no caminho de
   * quem só quer gravar.
   */
  async aplicarOsLida(entrada: {
    osLida: OsLida | null;
    motivoQuarentena: string | null;
    avisos: string[];
    arquivo: string;
    hashPdf: string;
    origem: 'agente' | 'upload';
    pdf: Uint8Array | null;
    imagem: Uint8Array | null;
    por: string | null;
    versaoLeitor: string;
  }): Promise<ResultadoDaImportacao> {
    const agora = new Date().toISOString();
    const cadastros = await this.carregarCadastros();

    // Mesmo arquivo, byte a byte, que JÁ ENTROU: é duplicata.
    //
    // Um arquivo que ficou na quarentena não conta: reprocessar depois de
    // corrigir o leitor é exatamente o caminho previsto (PRD §4.4), e tratá-lo
    // como duplicata deixaria a OS presa para sempre.
    const jaVisto = (
      await this.deposito.porIndice<Importacao>('importacao', 'hashPdf', entrada.hashPdf)
    ).filter((i) => i.status === 'aplicada' || i.status === 'duplicada');
    if (jaVisto.length > 0 && jaVisto[0]) {
      const anterior = jaVisto[0];
      const registro: Importacao = {
        id: novoId(),
        origem: entrada.origem,
        arquivo: entrada.arquivo,
        hashPdf: entrada.hashPdf,
        hashTexto: anterior.hashTexto,
        recebidoEm: agora,
        status: 'duplicada',
        osNumero: anterior.osNumero,
        versao: anterior.versao,
        versaoLeitor: entrada.versaoLeitor,
        erro: null,
        avisos: [],
        pdfRef: anterior.pdfRef,
        por: entrada.por,
      };
      await this.deposito.gravar('importacao', registro);
      this.invalidarProducao();
      return { importacao: registro, resultado: null, motivoQuarentena: null, avisos: [] };
    }

    const pdfRef = entrada.pdf ? await this.guardarArquivo('application/pdf', entrada.pdf) : null;

    if (!entrada.osLida || entrada.motivoQuarentena) {
      const registro: Importacao = {
        id: novoId(),
        origem: entrada.origem,
        arquivo: entrada.arquivo,
        hashPdf: entrada.hashPdf,
        hashTexto: entrada.osLida?.hashTexto ?? null,
        recebidoEm: agora,
        status: 'quarentena',
        osNumero: entrada.osLida?.cabecalho.osNumero ?? null,
        versao: null,
        versaoLeitor: entrada.versaoLeitor,
        erro: entrada.motivoQuarentena ?? 'Motivo não informado.',
        avisos: entrada.avisos,
        pdfRef,
        por: entrada.por,
      };
      await this.deposito.gravar('importacao', registro);
      await this.criarAlerta({
        tipo: 'importacao_quarentena',
        texto: `${entrada.arquivo}: ${registro.erro}`,
        importacaoId: registro.id,
      });
      this.invalidarProducao();
      return {
        importacao: registro,
        resultado: null,
        motivoQuarentena: registro.erro,
        avisos: entrada.avisos,
      };
    }

    const osLida = entrada.osLida;
    const numero = osLida.cabecalho.osNumero;
    const importacaoId = novoId();

    const osAtual = await this.deposito.ler<Os>('os', numero);
    let existente: Parameters<typeof importarOs>[0]['existente'] = null;
    if (osAtual) {
      const etapas = await this.deposito.porIndice<OsEtapa>('osEtapa', 'osNumero', numero);
      const versoes = await this.deposito.porIndice<OsVersao>('osVersao', 'osNumero', numero);
      const i = await this.carregarIndice();
      const comApontamento = new Set(
        etapas.filter((e) => (i.eventosPorEtapa.get(e.id)?.length ?? 0) > 0).map((e) => e.id),
      );
      existente = { os: osAtual, etapas, etapasComApontamento: comApontamento, versoes };
    }

    const cliente = await this.garantirCliente(osLida);
    const imagemRef = entrada.imagem
      ? await this.guardarArquivo('image/jpeg', entrada.imagem)
      : null;

    const resultado = importarOs({
      osLida,
      existente,
      regras: cadastros.dePara,
      estacoes: cadastros.estacoes,
      clienteId: cliente?.id ?? null,
      importacaoId,
      pdfRef,
      imagemFacaRef: imagemRef,
      agora,
    });

    if (resultado.tipo === 'duplicada') {
      const registro: Importacao = {
        id: importacaoId,
        origem: entrada.origem,
        arquivo: entrada.arquivo,
        hashPdf: entrada.hashPdf,
        hashTexto: osLida.hashTexto,
        recebidoEm: agora,
        status: 'duplicada',
        osNumero: numero,
        versao: resultado.versao,
        versaoLeitor: entrada.versaoLeitor,
        erro: null,
        avisos: entrada.avisos,
        pdfRef,
        por: entrada.por,
      };
      await this.deposito.gravar('importacao', registro);
      this.invalidarProducao();
      return { importacao: registro, resultado, motivoQuarentena: null, avisos: entrada.avisos };
    }

    // Aplicar. Planos, materiais e entregas são reescritos por inteiro: eles
    // são cópia fiel do PDF e não guardam decisão de produção nenhuma.
    await this.apagarPor('osPlano', numero);
    await this.apagarPor('osMaterial', numero);
    await this.apagarPor('osEntrega', numero);

    await this.deposito.gravar('os', resultado.os);
    await this.deposito.gravar('osVersao', resultado.versao);
    await this.deposito.gravarLote('osPlano', resultado.planos);
    await this.deposito.gravarLote('osEtapa', resultado.etapas);
    await this.deposito.gravarLote('osMaterial', resultado.materiais);
    await this.deposito.gravarLote('osEntrega', resultado.entregas);
    for (const id of resultado.etapasRemovidas) await this.deposito.apagar('osEtapa', id);

    const registro: Importacao = {
      id: importacaoId,
      origem: entrada.origem,
      arquivo: entrada.arquivo,
      hashPdf: entrada.hashPdf,
      hashTexto: osLida.hashTexto,
      recebidoEm: agora,
      status: 'aplicada',
      osNumero: numero,
      versao: resultado.versao.versao,
      versaoLeitor: entrada.versaoLeitor,
      erro: null,
      avisos: entrada.avisos,
      pdfRef,
      por: entrada.por,
    };
    await this.deposito.gravar('importacao', registro);
    this.enviar('importar_os_web', entrada.hashPdf, {
      p_json: osLida,
      p_hash_pdf: entrada.hashPdf,
      p_arquivo: entrada.arquivo,
      p_pdf_ref: pdfRef,
      p_imagem_ref: imagemRef,
      p_versao_leitor: entrada.versaoLeitor,
    });

    // ⚠️ O índice guardado é de ANTES desta importação. Sem descartá-lo aqui, a
    // conferência de divergência (abaixo) leria o status velho da OS.
    this.invalidarProducao();

    // Alertas que a importação levanta.
    if (resultado.etapasMarcadas.length > 0) {
      await this.criarAlerta({
        tipo: 'os_alterada_em_producao',
        osNumero: numero,
        texto:
          `OS ${numero} mudou depois que a produção começou. ` +
          (resultado.diff.length ? resultado.diff.join(' · ') : 'Confira as etapas marcadas.'),
      });
    }
    if (resultado.etapas.some((e) => e.aClassificar)) {
      await this.criarAlerta({
        tipo: 'estacao_a_classificar',
        osNumero: numero,
        texto: `OS ${numero} tem etapa sem estação definida. Classifique na triagem.`,
      });
    }
    if (resultado.os.concluidaNoErp) {
      const i = await this.carregarIndice();
      const estado = i.estadosOs.get(numero);
      if (estado && estado.status !== 'pronta' && estado.status !== 'concluida') {
        await this.criarAlerta({
          tipo: 'divergencia_erp',
          osNumero: numero,
          texto: `OS ${numero} está concluída no sistema da gráfica, mas não terminou aqui.`,
        });
      }
    }

    this.invalidarProducao();
    return {
      importacao: registro,
      resultado,
      motivoQuarentena: null,
      avisos: entrada.avisos,
    };
  }

  private async garantirCliente(osLida: OsLida): Promise<Cliente | null> {
    const codigo = osLida.cabecalho.clienteCodigo;
    const nome = osLida.cabecalho.clienteNome ?? osLida.cabecalho.clienteCurto;
    if (!codigo && !nome) return null;

    if (codigo) {
      const achados = await this.deposito.porIndice<Cliente>('cliente', 'codigoErp', codigo);
      const existente = achados[0];
      if (existente) {
        if (existente.nome !== nome || existente.nomeCurto !== osLida.cabecalho.clienteCurto) {
          const atualizado: Cliente = {
            ...existente,
            nome,
            nomeCurto: osLida.cabecalho.clienteCurto,
          };
          await this.deposito.gravar('cliente', atualizado);
          return atualizado;
        }
        return existente;
      }
    }

    const novo: Cliente = {
      id: novoId(),
      codigoErp: codigo ?? `sem-codigo-${nome}`,
      nome,
      nomeCurto: osLida.cabecalho.clienteCurto,
    };
    await this.deposito.gravar('cliente', novo);
    return novo;
  }

  private async apagarPor(colecao: keyof typeof COLECOES, osNumero: number): Promise<void> {
    const registros = await this.deposito.porIndice<{ id: string }>(colecao, 'osNumero', osNumero);
    for (const r of registros) await this.deposito.apagar(colecao, r.id);
  }

  /* ---------------- produção ---------------- */

  /**
   * Registra um apontamento.
   *
   * O evento chega com id já gerado pelo aparelho (RN-042): repetir a chamada
   * com o mesmo id não duplica nada. É o que permite ao tablet reenviar sem
   * medo quando a rede volta.
   */
  async registrarEvento(entrada: {
    id?: string;
    osEtapaId: string;
    tipo: TipoEvento;
    pessoaId: string | null;
    dispositivoId?: string | null;
    qtdBoa?: number | null;
    qtdRefugo?: number | null;
    motivoId?: string | null;
    texto?: string | null;
    ocorridoEm?: string;
    origem?: EventoProducao['origem'];
    estornaEventoId?: string | null;
    fotoRef?: string | null;
    /** Quando o operador já confirmou os avisos na tela. */
    confirmado?: boolean;
  }): Promise<RespostaDeEvento> {
    const i = await this.carregarIndice();
    const id = entrada.id ?? novoId();

    const jaExiste = await this.deposito.ler<EventoProducao>('evento', id);
    if (jaExiste) return { aceito: true, motivo: null, avisos: [], evento: jaExiste };

    const etapa = [...i.etapasPorOs.values()].flat().find((e) => e.id === entrada.osEtapaId);
    if (!etapa) {
      return {
        aceito: false,
        motivo: 'Esta etapa não existe mais. Sincronize e tente de novo.',
        avisos: [],
        evento: null,
      };
    }
    const os = i.oss.find((o) => o.numero === etapa.osNumero);
    if (!os) {
      return { aceito: false, motivo: 'A OS desta etapa não existe mais.', avisos: [], evento: null };
    }

    const pessoa = entrada.pessoaId
      ? (i.cadastros.pessoas.find((p) => p.id === entrada.pessoaId) ?? null)
      : null;
    if (entrada.pessoaId && !pessoa) {
      return { aceito: false, motivo: 'Operador não encontrado.', avisos: [], evento: null };
    }
    if (pessoa && !pessoa.ativa) {
      return { aceito: false, motivo: `${pessoa.nome} está inativo no cadastro.`, avisos: [], evento: null };
    }

    let avisos: Aviso[] = [];
    if (entrada.tipo === 'inicio' || entrada.tipo === 'retomada') {
      const etapasDaOs = i.etapasPorOs.get(os.numero) ?? [];
      const naEstacao = etapa.estacaoId
        ? [...i.etapasPorOs.values()]
            .flat()
            .filter(
              (e) =>
                e.estacaoId === etapa.estacaoId &&
                e.id !== etapa.id &&
                i.estadosEtapa.get(e.id)?.status === 'em_execucao',
            ).length
        : 0;
      const abertasDaPessoa = entrada.pessoaId
        ? [...i.estadosEtapa.entries()].filter(
            ([idEtapa, estado]) =>
              idEtapa !== etapa.id &&
              estado.status === 'em_execucao' &&
              estado.operadores.includes(entrada.pessoaId as string),
          ).length
        : 0;
      const estacao = i.cadastros.estacoes.find((e) => e.id === etapa.estacaoId);

      avisos = avisosAoIniciar({
        etapa,
        os,
        etapasDaOs,
        estados: i.estadosEtapa,
        pessoa,
        emExecucaoNaEstacao: naEstacao,
        capacidadeParalela: estacao?.capacidadeParalela ?? 1,
        etapasAbertasDaPessoa: abertasDaPessoa,
        parametros: i.cadastros.parametros,
      });

      if (temBloqueio(avisos)) {
        const bloqueio = avisos.find((a) => a.gravidade === 'bloqueio');
        return { aceito: false, motivo: bloqueio?.texto ?? 'Não é possível iniciar.', avisos, evento: null };
      }
      if (avisos.length > 0 && !entrada.confirmado) {
        return { aceito: false, motivo: null, avisos, evento: null };
      }
    }

    if (entrada.tipo === 'pausa') {
      const motivo = i.cadastros.motivos.find((m) => m.id === entrada.motivoId);
      if (!motivo) {
        return { aceito: false, motivo: 'Escolha o motivo da pausa.', avisos: [], evento: null };
      }
      if (motivo.exigeTexto && !entrada.texto?.trim()) {
        return {
          aceito: false,
          motivo: `O motivo "${motivo.descricao}" precisa de uma explicação.`,
          avisos: [],
          evento: null,
        };
      }
    }

    if (entrada.tipo === 'estorno') {
      if (!entrada.estornaEventoId) {
        return { aceito: false, motivo: 'Estorno sem evento de origem.', avisos: [], evento: null };
      }
      if (!entrada.texto?.trim()) {
        return { aceito: false, motivo: 'Diga o motivo do estorno.', avisos: [], evento: null };
      }
    }

    const ocorridoEm = entrada.ocorridoEm ?? new Date().toISOString();
    const recebidoEm = new Date().toISOString();
    const desvio = Math.round((Date.parse(recebidoEm) - Date.parse(ocorridoEm)) / 1000);

    const evento: EventoProducao = {
      id,
      osEtapaId: entrada.osEtapaId,
      tipo: entrada.tipo,
      pessoaId: entrada.pessoaId,
      dispositivoId: entrada.dispositivoId ?? null,
      estacaoId: etapa.estacaoId,
      qtdBoa: entrada.qtdBoa ?? null,
      qtdRefugo: entrada.qtdRefugo ?? null,
      motivoId: entrada.motivoId ?? null,
      texto: entrada.texto ?? null,
      ocorridoEm,
      recebidoEm,
      // Só faz sentido medir desvio de relógio quando o evento chega logo; um
      // evento que passou horas na fila offline tem desvio grande por motivo
      // legítimo (RN-025).
      desvioRelogioSeg: Math.abs(desvio) < 60 ? desvio : null,
      origem: entrada.origem ?? 'web',
      estornaEventoId: entrada.estornaEventoId ?? null,
      fotoRef: entrada.fotoRef ?? null,
    };

    await this.deposito.gravar('evento', evento);
    await this.atualizarStatusDaOs(os.numero);
    this.invalidarProducao();
    this.enviar('evento', evento.id, {
      id: evento.id,
      osEtapaId: evento.osEtapaId,
      tipo: evento.tipo,
      pessoaId: evento.pessoaId,
      dispositivoId: evento.dispositivoId,
      qtdBoa: evento.qtdBoa,
      qtdRefugo: evento.qtdRefugo,
      motivoId: evento.motivoId,
      texto: evento.texto,
      ocorridoEm: evento.ocorridoEm,
      origem: evento.origem,
      estornaEventoId: evento.estornaEventoId,
      fotoRef: evento.fotoRef,
    });

    return { aceito: true, motivo: null, avisos, evento };
  }

  /**
   * Grava no registro da OS o status derivado.
   *
   * O status continua sendo derivado — isto é só um espelho, para a lista poder
   * filtrar sem recalcular tudo. Quando os dois discordam, o derivado ganha.
   */
  private async atualizarStatusDaOs(numero: number): Promise<void> {
    const os = await this.deposito.ler<Os>('os', numero);
    if (!os) return;
    const etapas = await this.deposito.porIndice<OsEtapa>('osEtapa', 'osNumero', numero);
    const eventos: EventoProducao[] = [];
    for (const e of etapas) {
      eventos.push(...(await this.deposito.porIndice<EventoProducao>('evento', 'osEtapaId', e.id)));
    }
    const cadastros = await this.carregarCadastros();
    const agora = new Date().toISOString();
    const estados = new Map<string, EstadoEtapa>();
    for (const etapa of etapas) {
      estados.set(
        etapa.id,
        estadoDaEtapa(
          eventos.filter((ev) => ev.osEtapaId === etapa.id),
          { agora, quantidadePrevista: etapa.impressoesPrevistas, parametros: cadastros.parametros },
        ),
      );
    }
    const estado = estadoDaOs(os, etapas, { agora, estados });
    const atualizado: Os = {
      ...os,
      status: estado.status,
      prontaEm: estado.prontaEm ?? os.prontaEm,
    };
    await this.deposito.gravar('os', atualizado);
  }

  /* ---------------- ações do encarregado ---------------- */

  async definirPrioridade(numero: number, ligar: boolean): Promise<void> {
    const os = await this.deposito.ler<Os>('os', numero);
    if (!os) return;
    await this.deposito.gravar('os', {
      ...os,
      prioridadeEm: ligar ? new Date().toISOString() : null,
    });
    this.invalidarProducao();
    this.enviar('os_prioridade', os.id, { p_os: os.id, p_ligar: ligar });
  }

  async cancelarOs(numero: number, motivo: string, pessoaId: string | null): Promise<void> {
    const os = await this.deposito.ler<Os>('os', numero);
    if (!os) return;
    const agora = new Date().toISOString();

    // Encerra o que estiver correndo, para o tempo não seguir contando.
    const i = await this.carregarIndice();
    for (const etapa of i.etapasPorOs.get(numero) ?? []) {
      const estado = i.estadosEtapa.get(etapa.id);
      if (estado?.status === 'em_execucao') {
        await this.deposito.gravar<EventoProducao>('evento', {
          id: novoId(),
          osEtapaId: etapa.id,
          tipo: 'pausa',
          pessoaId,
          dispositivoId: null,
          estacaoId: etapa.estacaoId,
          qtdBoa: null,
          qtdRefugo: null,
          motivoId: null,
          texto: 'OS cancelada',
          ocorridoEm: agora,
          recebidoEm: agora,
          desvioRelogioSeg: 0,
          origem: 'sistema',
          estornaEventoId: null,
          fotoRef: null,
        });
      }
    }

    await this.deposito.gravar('os', {
      ...os,
      status: 'cancelada' as StatusOs,
      canceladaEm: agora,
      motivoCancelamento: motivo,
    });
    this.invalidarProducao();
    this.enviar('os_cancelar', os.id, { p_os: os.id, p_motivo: motivo });
  }

  async reabrirOs(numero: number): Promise<void> {
    const os = await this.deposito.ler<Os>('os', numero);
    if (!os) return;
    await this.deposito.gravar('os', {
      ...os,
      canceladaEm: null,
      motivoCancelamento: null,
      status: 'em_fila' as StatusOs,
    });
    await this.atualizarStatusDaOs(numero);
    this.invalidarProducao();
    this.enviar('os_reabrir', os.id, { p_os: os.id });
  }

  async registrarExpedicao(entrada: {
    osNumero: number;
    tipo: TipoExpedicao;
    quemLevou?: string | null;
    transportadora?: string | null;
    pessoaId: string | null;
    observacao?: string | null;
  }): Promise<void> {
    const agora = new Date().toISOString();
    await this.deposito.gravar<Expedicao>('expedicao', {
      id: novoId(),
      osNumero: entrada.osNumero,
      tipo: entrada.tipo,
      quemLevou: entrada.quemLevou ?? null,
      transportadora: entrada.transportadora ?? null,
      pessoaId: entrada.pessoaId,
      ocorridoEm: agora,
      observacao: entrada.observacao ?? null,
    });
    const os = await this.deposito.ler<Os>('os', entrada.osNumero);
    if (os) {
      await this.deposito.gravar('os', {
        ...os,
        concluidaEm: agora,
        status: 'concluida' as StatusOs,
      });
    }
    this.invalidarProducao();
    if (os) {
      this.enviar('expedicao_registrar', os.id, {
        p_os: os.id,
        p_tipo: entrada.tipo,
        p_quem: entrada.quemLevou ?? null,
        p_transportadora: entrada.transportadora ?? null,
        p_observacao: entrada.observacao ?? null,
      });
    }
  }

  /** Adiciona uma etapa que a OS não previu (RN-015). */
  async adicionarEtapa(entrada: {
    osNumero: number;
    nome: string;
    secao: Secao;
    estacaoId: string;
    tempoPrevistoSeg: number | null;
    origem?: 'manual' | 'retrabalho';
    etapaOrigemId?: string | null;
    observacoes?: string | null;
  }): Promise<OsEtapa> {
    const cadastros = await this.carregarCadastros();
    const estacao = cadastros.estacoes.find((e) => e.id === entrada.estacaoId);
    const existentes = await this.deposito.porIndice<OsEtapa>(
      'osEtapa',
      'osNumero',
      entrada.osNumero,
    );
    const sequencia =
      Math.max(-1, ...existentes.filter((e) => e.secao === entrada.secao).map((e) => e.sequencia)) +
      1;

    const os = await this.deposito.ler<Os>('os', entrada.osNumero);
    const etapa: OsEtapa = {
      id: novoId(),
      osNumero: entrada.osNumero,
      secao: entrada.secao,
      sequencia,
      nomeOs: entrada.nome,
      plano: null,
      equipamentoOs: estacao?.nome ?? null,
      estacaoId: entrada.estacaoId,
      setorId: estacao?.setorId ?? null,
      tipoLinha: 'operacao',
      aClassificar: false,
      impressoesPrevistas: null,
      unidade: null,
      tempoPrevistoSeg: entrada.tempoPrevistoSeg,
      observacoesOs: entrada.observacoes ?? null,
      origem: entrada.origem ?? 'manual',
      etapaOrigemId: entrada.etapaOrigemId ?? null,
      versaoCriada: os?.versaoAtual ?? 1,
      marcadaVerificar: false,
      ordemManual: null,
    };
    await this.deposito.gravar('osEtapa', etapa);
    await this.atualizarStatusDaOs(entrada.osNumero);
    this.invalidarProducao();
    if (os) {
      this.enviar('etapa_adicionar', etapa.id, {
        p_os: os.id,
        p_secao: entrada.secao,
        p_nome: entrada.nome,
        p_estacao: entrada.estacaoId,
        p_tempo_previsto_seg: entrada.tempoPrevistoSeg,
        p_origem: entrada.origem ?? 'manual',
        p_etapa_origem: entrada.etapaOrigemId ?? null,
        p_observacoes: entrada.observacoes ?? null,
      });
    }
    return etapa;
  }

  /** Retrabalho: repetir uma etapa já concluída, contando o tempo à parte. */
  async abrirRetrabalho(etapaId: string, motivo: string): Promise<OsEtapa | null> {
    const i = await this.carregarIndice();
    const original = [...i.etapasPorOs.values()].flat().find((e) => e.id === etapaId);
    if (!original || !original.estacaoId) return null;
    return this.adicionarEtapa({
      osNumero: original.osNumero,
      nome: `${original.nomeOs} (retrabalho)`,
      secao: original.secao,
      estacaoId: original.estacaoId,
      tempoPrevistoSeg: original.tempoPrevistoSeg,
      origem: 'retrabalho',
      etapaOrigemId: original.id,
      observacoes: motivo,
    });
  }

  async definirOrdemManual(etapaId: string, ordem: number | null): Promise<void> {
    const i = await this.carregarIndice();
    const etapa = [...i.etapasPorOs.values()].flat().find((e) => e.id === etapaId);
    if (!etapa) return;
    await this.deposito.gravar('osEtapa', { ...etapa, ordemManual: ordem });
    this.invalidarProducao();
    this.enviar('etapa_ordem', etapa.id, { p_etapa: etapa.id, p_ordem: ordem });
  }

  async limparMarcaDeVerificacao(etapaId: string): Promise<void> {
    const i = await this.carregarIndice();
    const etapa = [...i.etapasPorOs.values()].flat().find((e) => e.id === etapaId);
    if (!etapa) return;
    await this.deposito.gravar('osEtapa', { ...etapa, marcadaVerificar: false });
    this.invalidarProducao();
    this.enviar('etapa_limpar_marca', etapa.id, { p_etapa: etapa.id });
  }

  /**
   * Ensina o de-para e reclassifica de uma vez todas as etapas abertas com o
   * mesmo texto. O encarregado ensina uma vez; as próximas OS já chegam certas.
   */
  async aprenderDePara(entrada: {
    secao: Secao | null;
    equipamentoOs: string | null;
    nomeEtapa: string | null;
    estacaoId: string | null;
    tipoLinha: TipoLinha;
    por: string | null;
  }): Promise<number> {
    const regra: DeParaEstacao = {
      id: novoId(),
      secao: entrada.secao,
      equipamentoOs: entrada.equipamentoOs,
      nomeEtapa: entrada.nomeEtapa,
      estacaoId: entrada.estacaoId,
      tipoLinha: entrada.tipoLinha,
      aprendidoEm: new Date().toISOString(),
      aprendidoPor: entrada.por,
    };
    await this.deposito.gravar('dePara', regra);
    this.invalidar();
    this.enviar('de_para_aprender', regra.id, {
      p_secao: entrada.secao,
      p_equipamento: entrada.equipamentoOs,
      p_nome_etapa: entrada.nomeEtapa,
      p_estacao: entrada.estacaoId,
      p_tipo: entrada.tipoLinha,
    });
    return this.reclassificarEtapas();
  }

  /** Reaplica o de-para em todas as etapas abertas. Devolve quantas mudaram. */
  async reclassificarEtapas(): Promise<number> {
    const cadastros = await this.carregarCadastros();
    const etapas = await this.deposito.listar<OsEtapa>('osEtapa');
    const setorPorEstacao = new Map(cadastros.estacoes.map((e) => [e.id, e.setorId]));
    const mudadas: OsEtapa[] = [];

    for (const etapa of etapas) {
      if (etapa.origem !== 'os') continue;
      const c = classificar(
        { secao: etapa.secao, equipamentoOs: etapa.equipamentoOs, nomeEtapa: etapa.nomeOs },
        cadastros.dePara,
      );
      if (
        c.estacaoId === etapa.estacaoId &&
        c.tipoLinha === etapa.tipoLinha &&
        c.aClassificar === etapa.aClassificar
      ) {
        continue;
      }
      mudadas.push({
        ...etapa,
        estacaoId: c.estacaoId,
        setorId: c.estacaoId ? (setorPorEstacao.get(c.estacaoId) ?? null) : null,
        tipoLinha: c.tipoLinha,
        aClassificar: c.aClassificar,
      });
    }

    if (mudadas.length > 0) {
      await this.deposito.gravarLote('osEtapa', mudadas);
      // Fecha os alertas de triagem que deixaram de fazer sentido.
      const abertos = await this.alertas();
      const restantes = (await this.triagem()).length;
      if (restantes === 0) {
        for (const a of abertos) {
          if (a.tipo === 'estacao_a_classificar') await this.resolverAlerta(a.id, 'sistema');
        }
      }
      this.invalidarProducao();
    }
    return mudadas.length;
  }

  /* ---------------- alertas ---------------- */

  async criarAlerta(entrada: {
    tipo: TipoAlerta;
    texto: string;
    osNumero?: number | null;
    osEtapaId?: string | null;
    dispositivoId?: string | null;
    importacaoId?: string | null;
  }): Promise<Alerta> {
    // Não repetir alerta aberto do mesmo tipo para o mesmo alvo: alerta que
    // se acumula sozinho é alerta que ninguém lê.
    const abertos = await this.alertas();
    const igual = abertos.find((a) => mesmoAlvo(a, entrada));
    if (igual) return igual;

    const alerta = montarAlerta(entrada);
    await this.deposito.gravar('alerta', alerta);
    this.invalidarProducao();
    return alerta;
  }

  async resolverAlerta(id: string, por: unknown): Promise<void> {
    const a = await this.deposito.ler<Alerta>('alerta', id);
    if (!a || a.resolvidoEm) return;
    await this.deposito.gravar('alerta', {
      ...a,
      resolvidoEm: new Date().toISOString(),
      resolvidoPor: typeof por === 'string' ? por : null,
    });
    this.invalidarProducao();
    this.enviar('alerta_resolver', id, { p_alerta: id });
  }

  /**
   * Varre a produção e acerta os alertas do momento.
   *
   * **Não pode ser chamada de dentro de uma tela.** Ela escreve, escrever
   * avisa os ouvintes, e os ouvintes redesenham — se a tela a chamasse ao
   * desenhar, cada alerta novo provocaria um desenho, que provocaria outra
   * varredura. Foi assim que 11 OS atrasadas viraram 42 alertas: quatro
   * varreduras correndo ao mesmo tempo, cada uma lendo a lista antes de a
   * outra gravar. Quem chama é o arranque e o cronômetro, uma de cada vez.
   */
  async varrerAlertas(): Promise<void> {
    if (this.varrendo) return this.varrendo;
    this.varrendo = this.varrerAlertasAgora().finally(() => {
      this.varrendo = null;
    });
    return this.varrendo;
  }

  private varrendo: Promise<void> | null = null;

  private async varrerAlertasAgora(): Promise<void> {
    const i = await this.carregarIndice();
    const p = i.cadastros.parametros;

    const abertos = await this.alertas();
    const novos: Alerta[] = [];
    const aResolver: Alerta[] = [];

    // Tudo que a varredura decide fica neste índice local: assim uma passada
    // não cria dois alertas iguais nem depende do que já foi gravado.
    const vistos = new Map<string, Alerta>();
    for (const a of abertos) {
      const chave = chaveDoAlerta(a);
      const anterior = vistos.get(chave);
      if (anterior) aResolver.push(a); // duplicata herdada: some
      else vistos.set(chave, a);
    }

    const levantar = (entrada: Parameters<Repositorio['criarAlerta']>[0]): void => {
      const chave = chaveDoAlerta(entrada);
      if (vistos.has(chave)) return;
      const alerta = montarAlerta(entrada);
      vistos.set(chave, alerta);
      novos.push(alerta);
    };

    const deveContinuar = new Set<string>();

    for (const os of i.oss) {
      const estado = i.estadosOs.get(os.numero);
      if (!estado) continue;
      if (estado.status === 'concluida' || estado.status === 'cancelada') continue;

      if (osAtrasada(os, estado.status, p, i.agora)) {
        levantar({
          tipo: 'os_atrasada',
          osNumero: os.numero,
          texto: `OS ${os.numero} (${os.clienteCurto}) passou do prazo.`,
        });
        deveContinuar.add(chaveDoAlerta({ tipo: 'os_atrasada', osNumero: os.numero }));
      }

      for (const etapa of i.etapasPorOs.get(os.numero) ?? []) {
        if (etapa.tipoLinha !== 'operacao') continue;
        const e = i.estadosEtapa.get(etapa.id);
        if (!e) continue;
        if (
          e.status === 'em_execucao' &&
          etapa.tempoPrevistoSeg &&
          e.tempoRealSeg > (etapa.tempoPrevistoSeg * p.alertaEtapaPct) / 100
        ) {
          levantar({
            tipo: 'etapa_acima_do_previsto',
            osNumero: os.numero,
            osEtapaId: etapa.id,
            texto: `OS ${os.numero}: "${etapa.nomeOs}" passou de ${p.alertaEtapaPct}% do tempo previsto.`,
          });
          deveContinuar.add(
            chaveDoAlerta({ tipo: 'etapa_acima_do_previsto', osNumero: os.numero, osEtapaId: etapa.id }),
          );
        }
      }
    }

    // O que a condição deixou de justificar fecha sozinho.
    const semTriagem = (await this.triagem()).length === 0;
    for (const a of abertos) {
      if (aResolver.includes(a)) continue;
      if (a.tipo === 'os_atrasada' || a.tipo === 'etapa_acima_do_previsto') {
        if (!deveContinuar.has(chaveDoAlerta(a))) aResolver.push(a);
      }
      if (a.tipo === 'estacao_a_classificar' && semTriagem) aResolver.push(a);
    }

    if (novos.length === 0 && aResolver.length === 0) return;

    if (novos.length > 0) await this.deposito.gravarLote('alerta', novos);
    if (aResolver.length > 0) {
      const agora = new Date().toISOString();
      await this.deposito.gravarLote(
        'alerta',
        aResolver.map((a) => ({ ...a, resolvidoEm: agora, resolvidoPor: 'sistema' })),
      );
    }
    this.invalidarProducao();
  }
}

/** Dois alertas são o mesmo quando falam do mesmo assunto sobre o mesmo alvo. */
function chaveDoAlerta(a: {
  tipo: TipoAlerta;
  osNumero?: number | null;
  osEtapaId?: string | null;
  dispositivoId?: string | null;
  importacaoId?: string | null;
}): string {
  return [a.tipo, a.osNumero ?? '', a.osEtapaId ?? '', a.dispositivoId ?? '', a.importacaoId ?? ''].join('|');
}

function mesmoAlvo(
  a: Alerta,
  b: { tipo: TipoAlerta; osNumero?: number | null; osEtapaId?: string | null; dispositivoId?: string | null; importacaoId?: string | null },
): boolean {
  return chaveDoAlerta(a) === chaveDoAlerta(b);
}

function montarAlerta(entrada: {
  tipo: TipoAlerta;
  texto: string;
  osNumero?: number | null;
  osEtapaId?: string | null;
  dispositivoId?: string | null;
  importacaoId?: string | null;
}): Alerta {
  return {
    id: novoId(),
    tipo: entrada.tipo,
    osNumero: entrada.osNumero ?? null,
    osEtapaId: entrada.osEtapaId ?? null,
    dispositivoId: entrada.dispositivoId ?? null,
    importacaoId: entrada.importacaoId ?? null,
    texto: entrada.texto,
    criadoEm: new Date().toISOString(),
    resolvidoEm: null,
    resolvidoPor: null,
  };
}

/* ------------------------------------------------------------------ */

interface Indice {
  agora: string;
  oss: Os[];
  etapasPorOs: Map<number, OsEtapa[]>;
  eventosPorEtapa: Map<string, EventoProducao[]>;
  estadosEtapa: Map<string, EstadoEtapa>;
  estadosOs: Map<number, EstadoOs>;
  cadastros: Semente;
}

function estadoVazioDeEtapa(): EstadoEtapa {
  return {
    status: 'fila',
    iniciadaEm: null,
    concluidaEm: null,
    tempoRealSeg: 0,
    qtdBoa: null,
    qtdRefugo: null,
    operadores: [],
    motivoPausaId: null,
    marcas: [],
  };
}

function vazio(): EstadoOs {
  return {
    status: 'em_fila',
    prontaEm: null,
    etapasConcluidas: 0,
    etapasTotal: 0,
    tempoPrevistoRestanteSeg: 0,
    tempoPrevistoTotalSeg: 0,
    tempoRealTotalSeg: 0,
    etapaAtualId: null,
  };
}

/** Aberta antes de fechada; dentro de cada grupo, a que vence primeiro. */
function ordenarCarteira(a: LinhaCarteira, b: LinhaCarteira): number {
  const peso = (l: LinhaCarteira): number => {
    if (l.estado.status === 'cancelada') return 4;
    if (l.estado.status === 'concluida') return 3;
    if (l.estado.status === 'pronta') return 2;
    return l.atrasada ? 0 : 1;
  };
  const d = peso(a) - peso(b);
  if (d !== 0) return d;
  return porPrazo(a, b);
}

function porPrazo(a: LinhaCarteira, b: LinhaCarteira): number {
  if (a.os.prioridadeEm && !b.os.prioridadeEm) return -1;
  if (!a.os.prioridadeEm && b.os.prioridadeEm) return 1;
  const d = Date.parse(a.os.previsaoEntregaEm) - Date.parse(b.os.previsaoEntregaEm);
  return d !== 0 ? d : a.os.numero - b.os.numero;
}
